/**
 * Batch Alerts Component
 *
 * Displays alerts for expiring batches, expired batches, and low stock batches
 */

import {
  CalendarIcon,
  ClockIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useMemo } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InventoryItem } from '@/types/inventory';
import { formatDate, formatNumber } from '@/utils';

interface BatchAlertsProps {
  inventoryItems: InventoryItem[];
  onViewBatch?: (inventoryId: string, batchNumber: string) => void;
  className?: string;
}

interface BatchAlert {
  type: 'expiring' | 'expired' | 'critical';
  inventoryId: string;
  inventoryName: string;
  batchNumber: string;
  expiryDate: string;
  quantity: number;
  measurementUnit: string;
  daysLeft: number;
  qualityGrade?: string;
}

export const BatchAlerts: React.FC<BatchAlertsProps> = ({
  inventoryItems,
  onViewBatch,
  className = '',
}) => {
  // Process batch alerts
  const batchAlerts = useMemo(() => {
    const alerts: BatchAlert[] = [];
    const today = new Date();

    inventoryItems.forEach(item => {
      if (!item.batchTracking?.enabled || !item.batchTracking.batches) return;

      item.batchTracking.batches.forEach(batch => {
        const expiryDate = new Date(batch.expiryDate);
        const timeDiff = expiryDate.getTime() - today.getTime();
        const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

        let alertType: BatchAlert['type'];
        if (daysLeft < 0) {
          alertType = 'expired';
        } else if (daysLeft <= 3) {
          alertType = 'critical';
        } else if (daysLeft <= 7) {
          alertType = 'expiring';
        } else {
          return; // No alert needed
        }

        alerts.push({
          type: alertType,
          inventoryId: item._id!,
          inventoryName: item.name,
          batchNumber: batch.batchNumber,
          expiryDate: batch.expiryDate,
          quantity: batch.quantity,
          measurementUnit:
            typeof item.measurementUnit === 'string'
              ? item.measurementUnit
              : item.measurementUnit?.base || 'units',
          daysLeft,
          qualityGrade: batch.qualityGrade,
        });
      });
    });

    // Sort by urgency (expired first, then by days left)
    return alerts.sort((a, b) => {
      if (a.type === 'expired' && b.type !== 'expired') return -1;
      if (b.type === 'expired' && a.type !== 'expired') return 1;
      if (a.type === 'critical' && b.type === 'expiring') return -1;
      if (b.type === 'critical' && a.type === 'expiring') return 1;
      return a.daysLeft - b.daysLeft;
    });
  }, [inventoryItems]);

  // Group alerts by type
  const alertsByType = useMemo(() => {
    const grouped = {
      expired: batchAlerts.filter(alert => alert.type === 'expired'),
      critical: batchAlerts.filter(alert => alert.type === 'critical'),
      expiring: batchAlerts.filter(alert => alert.type === 'expiring'),
    };

    return grouped;
  }, [batchAlerts]);

  // Alert type configurations
  const alertConfig = {
    expired: {
      title: 'Expired Batches',
      icon: XCircleIcon,
      iconColor: 'text-red-500',
      badgeColor: 'bg-red-100 text-red-800',
      cardBorder: 'border-red-200',
      description:
        'These batches have already expired and should be removed from inventory.',
    },
    critical: {
      title: 'Critical - Expiring Soon',
      icon: WarningIcon,
      iconColor: 'text-red-500',
      badgeColor: 'bg-red-100 text-red-800',
      cardBorder: 'border-red-200',
      description:
        'These batches expire within 3 days and need immediate attention.',
    },
    expiring: {
      title: 'Expiring This Week',
      icon: ClockIcon,
      iconColor: 'text-orange-500',
      badgeColor: 'bg-orange-100 text-orange-800',
      cardBorder: 'border-orange-200',
      description: 'These batches will expire within 7 days.',
    },
  };

  // Handle view batch
  const handleViewBatch = (alert: BatchAlert) => {
    onViewBatch?.(alert.inventoryId, alert.batchNumber);
  };

  // Render alert item
  const renderAlertItem = (alert: BatchAlert) => {
    const config = alertConfig[alert.type];

    return (
      <div
        key={`${alert.inventoryId}-${alert.batchNumber}`}
        className='flex items-start justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors'
      >
        <div className='flex items-start gap-3 flex-1'>
          <config.icon className={`h-5 w-5 mt-0.5 ${config.iconColor}`} />
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 mb-1'>
              <h4 className='font-medium text-gray-900 truncate'>
                {alert.inventoryName}
              </h4>
              <Badge className={config.badgeColor}>{alert.batchNumber}</Badge>
              {alert.qualityGrade && (
                <Badge variant='outline'>Grade {alert.qualityGrade}</Badge>
              )}
            </div>
            <div className='text-sm text-gray-600 space-y-1'>
              <div className='flex items-center gap-4'>
                <span className='flex items-center gap-1'>
                  <CalendarIcon className='h-4 w-4' />
                  Expires: {formatDate(alert.expiryDate)}
                </span>
                <span>
                  Quantity: {formatNumber(alert.quantity)}{' '}
                  {alert.measurementUnit}
                </span>
              </div>
              <div className='font-medium'>
                {alert.type === 'expired' ? (
                  <span className='text-red-600'>
                    Expired {Math.abs(alert.daysLeft)} days ago
                  </span>
                ) : (
                  <span
                    className={
                      alert.type === 'critical'
                        ? 'text-red-600'
                        : 'text-orange-600'
                    }
                  >
                    {alert.daysLeft} day{alert.daysLeft !== 1 ? 's' : ''}{' '}
                    remaining
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <Button
          hierarchy='outlined'
          size='sm'
          onClick={() => handleViewBatch(alert)}
        >
          View
        </Button>
      </div>
    );
  };

  // Render alert section
  const renderAlertSection = (type: keyof typeof alertsByType) => {
    const alerts = alertsByType[type];
    const config = alertConfig[type];

    if (alerts.length === 0) return null;

    return (
      <Card key={type} className={`${config.cardBorder} border-l-4`}>
        <CardHeader className='pb-3'>
          <CardTitle className='flex items-center gap-2 text-base'>
            <config.icon className={`h-5 w-5 ${config.iconColor}`} />
            {config.title}
            <Badge className={config.badgeColor}>{alerts.length}</Badge>
          </CardTitle>
          <p className='text-sm text-gray-600'>{config.description}</p>
        </CardHeader>
        <CardContent>
          <ScrollArea className='max-h-64'>
            <div className='space-y-3'>{alerts.map(renderAlertItem)}</div>
          </ScrollArea>
        </CardContent>
      </Card>
    );
  };

  if (batchAlerts.length === 0) {
    return (
      <Card className={className}>
        <CardContent className='p-6'>
          <div className='text-center'>
            <ClockIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
            <h3 className='font-medium text-gray-900 mb-2'>No Batch Alerts</h3>
            <p className='text-gray-600'>
              All batches are within their optimal shelf life.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary Alert */}
      <Alert>
        <WarningIcon className='h-4 w-4' />
        <AlertDescription>
          <strong>
            {batchAlerts.length} batch alert
            {batchAlerts.length !== 1 ? 's' : ''}
          </strong>
          {' - '}
          {alertsByType.expired.length > 0 &&
            `${alertsByType.expired.length} expired, `}
          {alertsByType.critical.length > 0 &&
            `${alertsByType.critical.length} critical, `}
          {alertsByType.expiring.length > 0 &&
            `${alertsByType.expiring.length} expiring soon`}
        </AlertDescription>
      </Alert>

      {/* Alert Sections */}
      {renderAlertSection('expired')}
      {renderAlertSection('critical')}
      {renderAlertSection('expiring')}
    </div>
  );
};
